/**
 * Cmarkdowncode Component
 * Code component for markdown rendering (inline and block code)
 */

"use client";

import { useCallback, useState } from "react";
import { Cmarkdownvisual } from "../Cmarkdownvisual";
import { pxToRem } from "@/theme/spacing";
import { CmarkdowncodeBlock } from "./CmarkdowncodeBlock";
import { CmarkdowncodeCompactPreview } from "./CmarkdowncodeCompactPreview";
import { CmarkdowncodeControls } from "./CmarkdowncodeControls";
import { CmarkdowncodeInline } from "./CmarkdowncodeInline";
import type { CmarkdowncodeProps } from "./Cmarkdowncode.types";
import {
  CODE_DOWNLOAD_MIME_TYPE,
  CODE_METRIC_UNIT_PLURAL,
  CODE_METRIC_UNIT_SINGLE,
  COMPACT_PREVIEW_LINE_LIMIT,
  DEFAULT_FONT_SIZE_PX,
  FONT_SIZE_STEP_PX,
  MAX_FONT_SIZE_PX,
  MIN_FONT_SIZE_PX,
  VISUAL_CONTENT_PADDING_PX,
} from "./cmarkdowncodeConstants";
import {
  resolveDisplayName,
  resolveDownloadFilename,
} from "./cmarkdowncodeLanguage";

export type { CmarkdowncodeProps } from "./Cmarkdowncode.types";

export function Cmarkdowncode({
  children,
  inline = false,
  language,
  className,
  style,
  concaved = false,
  defaultExpanded,
}: CmarkdowncodeProps) {
  const blockCode =
    typeof children === "string" ? children : String(children ?? "");
  const normalizedLanguage = language?.trim();
  const [fontSizePx, setFontSizePx] = useState<number>(DEFAULT_FONT_SIZE_PX);

  const handleCopy = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText)
      return;
    navigator.clipboard.writeText(blockCode).catch(() => {});
  }, [blockCode]);

  const handleDownload = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    const filename = resolveDownloadFilename(normalizedLanguage);
    const blob = new Blob([blockCode], { type: CODE_DOWNLOAD_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [blockCode, normalizedLanguage]);

  const handleFontDecrease = useCallback(() => {
    setFontSizePx((prev) =>
      Math.max(MIN_FONT_SIZE_PX, prev - FONT_SIZE_STEP_PX),
    );
  }, []);

  const handleFontIncrease = useCallback(() => {
    setFontSizePx((prev) =>
      Math.min(MAX_FONT_SIZE_PX, prev + FONT_SIZE_STEP_PX),
    );
  }, []);

  if (inline) {
    return (
      <CmarkdowncodeInline className={className} style={style}>
        {children}
      </CmarkdowncodeInline>
    );
  }

  const allLines = blockCode.split("\n");
  const previewLines = allLines.slice(0, COMPACT_PREVIEW_LINE_LIMIT).join("\n");
  const lineCount = allLines.length;
  const metricUnit =
    lineCount === 1 ? CODE_METRIC_UNIT_SINGLE : CODE_METRIC_UNIT_PLURAL;
  const controls = (
    <CmarkdowncodeControls
      fontSizePx={fontSizePx}
      onCopy={handleCopy}
      onDownload={handleDownload}
      onFontDecrease={handleFontDecrease}
      onFontIncrease={handleFontIncrease}
    />
  );
  const modalControls = (
    <CmarkdowncodeControls
      fontSizePx={fontSizePx}
      onCopy={handleCopy}
      onDownload={handleDownload}
      onFontDecrease={handleFontDecrease}
      onFontIncrease={handleFontIncrease}
    />
  );
  const compactPreview = (
    <CmarkdowncodeCompactPreview
      code={previewLines}
      language={normalizedLanguage}
    />
  );

  return (
    <Cmarkdownvisual
      type={resolveDisplayName(normalizedLanguage)}
      className={className}
      style={style}
      contentStyle={{ padding: pxToRem(VISUAL_CONTENT_PADDING_PX) }}
      compactPreview={compactPreview}
      metric={{ value: lineCount, unit: metricUnit }}
      controls={controls}
      modalControls={modalControls}
      concaved={concaved}
      defaultExpanded={defaultExpanded}
    >
      <CmarkdowncodeBlock
        code={blockCode}
        language={normalizedLanguage}
        fontSizePx={fontSizePx}
      />
    </Cmarkdownvisual>
  );
}
